import { FormattedMessage } from 'react-intl';

import { BankAccountVerificationOption } from './BankAccountVerificationOption';
import styles from './ChooseNewBankAccountVerification.module.css';
import { TNewBankAccountModalName } from '../verifyNewBankAccountModal/VerifyNewBankAccountModal';

export const ChooseNewBankAccountVerification = ({
    changeVerificationMethod,
    isBankEvidence = false
}: {
    changeVerificationMethod: (method: TNewBankAccountModalName) => void;
    isBankEvidence?: boolean;
}) => {
    return (
        <>
            {isBankEvidence ? (
                <div className={styles.chooseVerificationMethodDescription}>
                    <p>
                        <strong>
                            <FormattedMessage
                                id="_.funding_requirements.bank_evidence.choose_verification_method_heading"
                                defaultMessage="We need to see the last 3 months of transactions from your main business bank account."
                            />
                        </strong>
                    </p>
                    <p>
                        <FormattedMessage
                            id="_.funding_requirements.bank_evidence.choose_verification_method_open_banking"
                            defaultMessage="We’re <fcaLink>FCA regulated</fcaLink> which means we can use Open Banking. Open Banking with TrueLayer is a secure way to share your account information without sharing your password. <obLink>Learn more</obLink>"
                            values={{
                                fcaLink: (chunks) => (
                                    <a
                                        href="https://www.fca.org.uk/about/what-we-do/the-fca"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline"
                                    >
                                        {chunks}
                                    </a>
                                ),
                                obLink: (chunks) => (
                                    <a
                                        href="https://truelayer.com/openbanking/what-is-open-banking/"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline"
                                    >
                                        {chunks}
                                    </a>
                                )
                            }}
                        />
                    </p>
                    <p>
                        <FormattedMessage
                            id="_.funding_requirements.bank_evidence.choose_verification_method_transactions"
                            defaultMessage="Your transactions must cover the full 3-month period and be from the account(s) where the majority of your business revenue is received."
                        />
                    </p>
                </div>
            ) : (
                <p className={styles.chooseVerificationMethodDescription}>
                    <FormattedMessage
                        id="_.funding_requirements.add_bank_details.choose_verification_method_heading"
                        defaultMessage="We will use this account for loan disbursement and direct debit collection of your monthly repayments."
                    />
                </p>
            )}
            <div className={styles.verificationOptions}>
                <BankAccountVerificationOption
                    header={
                        isBankEvidence ? (
                            <FormattedMessage
                                id="_.funding_requirements.bank_evidence.online_banking_option.title"
                                defaultMessage="Connect via Open Banking"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.funding_requirements.add_bank_details.choose_verification_method_online_banking.title"
                                defaultMessage="Verify online"
                            />
                        )
                    }
                    message={
                        isBankEvidence ? (
                            <FormattedMessage
                                id="_.funding_requirements.bank_evidence.online_banking_option.message"
                                defaultMessage="Link your account in just a few clicks via Open Banking. Your transaction history is shared securely, we never see your login details."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.funding_requirements.add_bank_details.choose_verification_method_online_banking.message"
                                defaultMessage="Link your account in just a few clicks via Open Banking. Only your account number and name will be shared with us."
                            />
                        )
                    }
                    recommended={true}
                    onClick={() => {
                        changeVerificationMethod('open_banking');
                    }}
                />
                <BankAccountVerificationOption
                    header={
                        <FormattedMessage
                            id="_.funding_requirements.add_bank_details.choose_verification_method_manual_upload.title"
                            defaultMessage="Upload a statement"
                        />
                    }
                    message={
                        isBankEvidence ? (
                            <FormattedMessage
                                id="_.funding_requirements.bank_evidence.manual_upload_option.message"
                                defaultMessage="Upload your bank statements as PDF."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.funding_requirements.add_bank_details.choose_verification_method_manual_upload.message"
                                defaultMessage="Manually upload a bank statement to verify your bank details."
                            />
                        )
                    }
                    recommended={false}
                    onClick={() => {
                        changeVerificationMethod('manual_bs_upload');
                    }}
                />
            </div>
        </>
    );
};
